import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import styled from 'styled-components';
import ContextSnackbar from 'Components/Snackbar';
import { HeadingTwo } from 'Components/Headings';
import { VENUES } from 'Queries/SuperAdmin';
import { SnackbarContext } from 'Store/SnackbarContext';
import { subRouteCodes as SUB_ROUTES } from 'Constants/routes';
import { CREATE_DISCOUNT_CODE_FOR_VENUE } from 'Mutations/DiscountCodes';
import DiscountCodeForm from 'Components/DiscountCodes/DiscountCodeForm';
import { displayFlex } from '../../../styles/Mixins';
import { paragraphReg } from '../../../styles/Typography';
import colors from '../../../styles/Colors';

const PageContainer = styled.div`
  &&& {
    ${displayFlex}
    align-items: center;
    width: 100%;
    height: auto;
    padding: 12px 0 11px 20px;
    ${paragraphReg}
    font-size: 14px;
    text-align: left;
    background-color: ${colors.background.primary};
    color: ${colors.text};
    border-radius: 3px;
  }
`;

const PageHeader = styled.div`
  text-align: left;
  width: 100%;
  border-bottom: 1px solid rgb(200, 214, 229);
  &&& {
    margin: 65px 0 25px;
  }
`;

const HeaderWrapper = styled.div`
  margin: 20px 0px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PageBody = styled.div`
  text-align: left;
  width: 100%;
  &&& {
    margin: 0;
    padding: 20px;
  }
`;

const CreateDiscountCode = () => {
  const [venues, setVenues] = useState([]);
  const { showSnackbar } = useContext(SnackbarContext);
  const history = useHistory();

  const { data: venuesData, loading: venuesLoading } = useQuery(VENUES, {
    fetchPolicy: 'network-only'
  });

  const [createDiscountCode, { loading: createLoading }] = useMutation(CREATE_DISCOUNT_CODE_FOR_VENUE, {
    onCompleted: data => {
      if (data.createDiscountCodeForVenue.success) {
        showSnackbar(`Discount code "${data.createDiscountCodeForVenue.discountCode.code}" created successfully`, { error: false, duration: 5000 });
        history.push(SUB_ROUTES.SUPER_ADMIN.DISCOUNT_CODES);
      } else {
        showSnackbar(data.createDiscountCodeForVenue.error || 'Unable to create discount code', { error: true, duration: 5000 });
      }
    },
    onError: error => {
      showSnackbar(error?.message?.replace('GraphQL error: ', '') || 'Unable to create discount code', { error: true, duration: 5000 });
    }
  });

  useEffect(() => {
    if (venuesData) {
      const sortedVenues = [...venuesData.venues].sort((a, b) => +a.id - +b.id);
      setVenues(sortedVenues);
    }
  }, [venuesData]);

  const handleSubmit = async values => {
    // Prepare the input for the mutation
    const input = {
      venueId: values.venueId,
      code: values.code,
      discountType: values.discountType,
      discountPercentage: values.discountType === 'percentage' ? values.discountPercentage : null,
      discountAmount: values.discountType === 'fixed_amount' ? values.discountAmount : null,
      validFrom: values.validFrom,
      validUntil: values.validUntil,
      maxUses: values.maxUses || null,
      minOrderAmount: values.minOrderAmount || null,
      description: values.description || null
    };

    await createDiscountCode({
      variables: { input }
    });
  };

  const handleCancel = () => {
    history.push(SUB_ROUTES.SUPER_ADMIN.DISCOUNT_CODES);
  };

  return (
    <>
      <PageContainer>
        <ContextSnackbar />
        <PageHeader>
          <HeaderWrapper>
            <HeadingTwo label="CREATE DISCOUNT CODE" />
          </HeaderWrapper>
        </PageHeader>
      </PageContainer>

      <PageBody>
        <DiscountCodeForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={createLoading || venuesLoading}
          venues={venues}
          submitText="CREATE DISCOUNT CODE"
          showVenueSelector={true}
        />
      </PageBody>
    </>
  );
};

export default CreateDiscountCode;
